import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import axios, { type AxiosRequestConfig, type AxiosError } from 'axios';

import { env } from '@/config/env';
import type { ApiError } from '@/types/api';
import { extractAxiosResponseDataMessage } from '@/utils/getApiErrorMessage';

interface AxiosBaseQueryArgs {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
  responseType?: AxiosRequestConfig['responseType'];
}

/**
 * Base URL cho dịch vụ Geo-processing (FastAPI).
 * Ưu tiên VITE_GEO_PROCESSING_URL, fallback sang API_BASE_URL (gateway).
 */
const geoProcessingBaseUrl = env.GEO_PROCESSING_URL || env.API_BASE_URL;

const geoProcessingApiClient = axios.create({
  baseURL: geoProcessingBaseUrl,
  timeout: 60000, // Analysis tasks can take longer
  headers: { 'Content-Type': 'application/json' },
});

const geoProcessingBaseQuery: BaseQueryFn<AxiosBaseQueryArgs, unknown, ApiError> = async ({
  url,
  method = 'GET',
  data,
  params,
  headers,
  responseType,
}) => {
  try {
    const result = await geoProcessingApiClient({
      url,
      method,
      data,
      params,
      headers,
      responseType,
    });
    return { data: result.data };
  } catch (err) {
    const axiosError = err as AxiosError<Record<string, unknown>>;
    const response = axiosError.response;
    const data = response?.data;
    return {
      error: {
        status: response?.status ?? 0,
        message: extractAxiosResponseDataMessage(data, axiosError.message),
        errors:
          data && typeof data === 'object' && 'errors' in data
            ? (data.errors as Record<string, string[]>)
            : undefined,
        noResponse: !response,
      },
    };
  }
};

export const geoProcessingApi = createApi({
  reducerPath: 'geoProcessingApi',
  baseQuery: geoProcessingBaseQuery,
  tagTypes: ['FloodJob', 'NetcdfJob'],
  endpoints: () => ({}),
});
